import {
  FieldValues,
  DeepMap,
  FieldError,
  Controller,
  Control,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTime } from 'luxon';

interface TimeInputProps {
  label: string;
  name: string;
  control: Control<any>;
  required?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  defaultValue?: any;
  className?: string;
  slotProps?: any;
  disableIgnoringDatePartForTimeValidation?: boolean;
  minTime?: DateTime;
  maxTime?: DateTime;
  minTimeErrorMessage?: string;
  maxTimeErrorMessage?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onFocus?: () => void | null;
  onOpen?: () => void | null;
  timezone?: string;
  displayValue?: DateTime | null;
  onTimeSelect?: (date: DateTime | null) => void;
}

export default function TimeInput({
  label,
  name,
  control,
  required = false,
  errors,
  defaultValue = undefined,
  disableIgnoringDatePartForTimeValidation = false,
  minTime = undefined,
  maxTime = undefined,
  minTimeErrorMessage = undefined,
  maxTimeErrorMessage = undefined,
  disabled = false,
  readOnly = false,
  onFocus = undefined,
  onOpen = undefined,
  displayValue,
  onTimeSelect,
  ...otherProps
}: TimeInputProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const invalidDateMessage = t('common.invalidDate');

  const handleChange = (date: any, changeFn: (val: any) => void) => {
    changeFn(date);
  };

  const validateDate = (time: DateTime): boolean | string => {
    if (displayValue === null && required) {
      return requiredFieldMessage;
    }
    if (required && !time?.isValid) {
      return invalidDateMessage;
    }
    if (!required && time && !time.isValid) {
      return invalidDateMessage;
    }
    if (minTime && time && time < minTime) {
      const msg = minTimeErrorMessage
        ? minTimeErrorMessage
        : t('common.timeTooEarly');

      return msg;
    }
    if (maxTime && time && time > maxTime) {
      const msg = maxTimeErrorMessage
        ? maxTimeErrorMessage
        : t('common.timeTooLate');
      return msg;
    }
    return true;
  };

  return (
    <Controller
      name={name as any}
      control={control}
      defaultValue={defaultValue}
      rules={{
        required: required && requiredFieldMessage,
        validate: validateDate,
      }}
      render={({ field }: { field: any }) => {
        return (
          <TimePicker
            label={label}
            value={displayValue !== undefined ? displayValue : field.value}
            slotProps={{
              textField: {
                variant: 'outlined',
                onFocus: () => {
                  onFocus && onFocus();
                },
                required: required,
              },
            }}
            disableIgnoringDatePartForTimeValidation={
              disableIgnoringDatePartForTimeValidation
            }
            minTime={minTime}
            maxTime={maxTime}
            disabled={disabled}
            readOnly={readOnly}
            onChange={(date) => {
              if (onTimeSelect) {
                onTimeSelect(date);
              } else {
                handleChange(date, field.onChange);
              }
              if (date) {
                field.onBlur();
              }
            }}
            closeOnSelect={false}
            onOpen={onOpen}
            {...otherProps}
          />
        );
      }}
    />
  );
}
